<template>
  <div class="w-[29rem] mb-4">
    <NText variant="caption1" weight="bold" class="pr-4"
      >{{ t('modules.order.microTransactions') }}
    </NText>

    <div
      class="flex justify-between items-center bg-neutral-secondary my-1 px-4 py-3 rounded-md"
    >
      <NText variant="caption1" weight="medium">
        {{ t('modules.order.AverageTransactionPrice') }}
      </NText>

      <span>
        <NumberFormatter
          #="{ number }"
          :value="
            omsAverageTransactionPrice(
              microTransactionsValueNoWage,
              microTransactionsQuantities
            )
          "
          round
        >
          <NText variant="caption1" weight="bold">
            {{ number }}
          </NText>
        </NumberFormatter>
        <NText variant="overline" weight="medium">{{ t('misc.rial') }}</NText>
      </span>
    </div>

    <div
      class="flex justify-between items-center bg-neutral-secondary my-1 px-4 py-3 rounded-md"
    >
      <NText variant="caption1" weight="medium">
        {{ t('modules.order.microTransactionsValue') }}
      </NText>

      <span>
        <NumberFormatter #="{ number }" :value="microTransactionsValue" round>
          <NText variant="caption1" weight="bold">
            {{ number }}
          </NText>
        </NumberFormatter>
        <NText variant="overline" weight="medium">{{ t('misc.rial') }}</NText>
      </span>
    </div>

    <div class="p-4 bg-neutral-secondary rounded-md">
      <MicroTransactionTable :trades="microTransactionsList" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import {
  useGetMicroTransactionsQuery,
  omsAverageTransactionPrice,
} from '@core/order';
import { OrderItem } from '@libs/api-reference';
import { computed } from 'vue';

import NumberFormatter from '@/components/Number.vue';

import MicroTransactionTable from './MicroTransactionTable.vue';

type MicroTransactionProps = {
  order: OrderItem;
  isLegacy: boolean;
};

const props = defineProps<MicroTransactionProps>();

const { t } = useI18n();

const { microTransactionsList } = useGetMicroTransactionsQuery(
  props.order.orderId,
  props.isLegacy
);

const microTransactionsValue = computed(() => {
  return microTransactionsList.value.reduce<number>(
    (previousValue, currentValue) => {
      if (currentValue.value) {
        return previousValue + currentValue?.value;
      }

      return previousValue;
    },
    0
  );
});

const microTransactionsValueNoWage = computed(() => {
  return microTransactionsList.value.reduce<number>(
    (previousValue, currentValue) => {
      if (currentValue.price && currentValue.quantity) {
        return previousValue + currentValue.price * currentValue.quantity;
      }

      return previousValue;
    },
    0
  );
});

const microTransactionsQuantities = computed(() => {
  return microTransactionsList.value.reduce<number>(
    (previousValue, currentValue) => {
      if (currentValue.quantity) {
        return previousValue + currentValue?.quantity;
      }

      return previousValue;
    },
    0
  );
});
</script>
